import { Box, Typography, Card, CardContent, Button, Modal, Backdrop, Fade, Paper, IconButton, TextField } from "@mui/material";
import React, { Fragment, useState } from "react";
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import CategoryIcon from '@mui/icons-material/Category';
import AssignmentIcon from '@mui/icons-material/Assignment';
import InventoryIcon from '@mui/icons-material/Inventory';
import StoreIcon from '@mui/icons-material/Store';
import DownloadIcon from '@mui/icons-material/Download';
import { showNotification } from "../../../../common/Notification/ToastifyNotification";
import { apiRequest } from "../../../../common/common";
import { useSelector } from "react-redux";

const ImportData = () => {
    const { user } = useSelector(state => state.users);
    const [openModal, setOpenModal] = useState(false);
    const [selectedBox, setSelectedBox] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [sheetName, setSheetName] = useState("");
    const [sheetNameError, setSheetNameError] = useState("");

    // Allowed file types
    const allowedFileTypes = ['.csv', '.xls', '.xlsx'];

    // Box configuration
    const boxes = [
        {
            id: 'product_provider',
            title: 'Import Product Provider',
            icon: <StoreIcon sx={{ fontSize: 40 }} />,
            color: '#1976d2',
            apiEndpoint: '/api/import/product-provider',
            apiMethod: 'importProductProvider',
            requiresSheetName: false,
            sampleFile: '/sample-Import-Product-Provider.xlsx'   // TODO: replace with actual file path
        },
        {
            id: 'categories',
            title: 'Import Categories',
            icon: <CategoryIcon sx={{ fontSize: 40 }} />,
            color: '#2e7d32',
            apiEndpoint: '/api/import/categories',
            apiMethod: 'importCategories',
            requiresSheetName: false,
            sampleFile: '/sample-categories-mvedas.xlsx'         // TODO: replace with actual file path
        },
        {
            id: 'template_dept_rooms',
            title: 'Import Template Department + Rooms',
            icon: <AssignmentIcon sx={{ fontSize: 40 }} />,
            color: '#ed6c02',
            apiEndpoint: '/api/import/template-dept-rooms',
            apiMethod: 'importTemplateDeptRooms',
            requiresSheetName: true,
            sampleFile: '/sample-template-department-room.xlsx' // TODO: replace with actual file path
        },
        {
            id: 'template_item',
            title: 'Import Template Item',
            icon: <DescriptionIcon sx={{ fontSize: 40 }} />,
            color: '#9c27b0',
            apiEndpoint: '/api/import/template-item',
            apiMethod: 'importTemplateItem',
            requiresSheetName: true,
            sampleFile: '/sample-template-items.xlsx'       // TODO: replace with actual file path
        },
        {
            id: 'product',
            title: 'Import Product',
            icon: <InventoryIcon sx={{ fontSize: 40 }} />,
            color: '#d32f2f',
            apiEndpoint: '/api/import/product',
            apiMethod: 'importProduct',
            requiresSheetName: false,
            sampleFile: '/sample_product_import.xlsx'             // TODO: replace with actual file path
        }
    ];

    // Different API call functions based on box type
    const apiCalls = {
        importProductProvider: async (file, formData, sheetNameValue) => {
            formData.append('user_id', user?.user_id);
            formData.append('organization_id', user?.organization?.organization_id);
            const response = await apiRequest('post', `/product-provider/import`, formData);
            return response;
        },

        importCategories: async (file, formData, sheetNameValue) => {
            formData.append('organization_id', user?.organization?.organization_id);
            const response = await apiRequest('post', `/category/import`, formData);
            return response;
        },

        importTemplateDeptRooms: async (file, formData, sheetNameValue) => {
            formData.append('type', 'public');
            formData.append('user_d', user?.user_id);
            formData.append('sheet_name', sheetNameValue);
            formData.append('organization_id', user?.organization?.organization_id);
            const response = await apiRequest('post', `/template/import`, formData);
            return response;
        },

        importTemplateItem: async (file, formData, sheetNameValue) => {
            formData.append('user_d', user?.user_id);
            formData.append('sheet_name', sheetNameValue);
            formData.append('organization_id', user?.organization?.organization_id);
            const response = await apiRequest('post', `/template/import-items`, formData);
            return response;
        },

        importProduct: async (file, formData, sheetNameValue) => {
            formData.append('organization_id', user?.organization?.organization_id);
            const response = await apiRequest('post', `/product/import-products`, formData);
            return response;
        }
    };

    const handleBoxClick = (box) => {
        setSelectedBox(box);
        setOpenModal(true);
        setSelectedFile(null);
        setSheetName("");
        setSheetNameError("");
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setSelectedBox(null);
        setSelectedFile(null);
        setSheetName("");
        setSheetNameError("");
    };

    // ── Sample file download ───────────────────────────────────────────────────
    const handleSampleDownload = (e, box) => {
        e.stopPropagation(); // prevent card click opening the upload modal
        const link = document.createElement('a');
        link.href = box.sampleFile;
        link.download = box.sampleFile.split('/').pop();
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const validateFileType = (file) => {
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
        return allowedFileTypes.includes(fileExtension);
    };

    const getFileTypeMessage = () => {
        return allowedFileTypes.join(', ').toUpperCase();
    };

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            if (validateFileType(file)) {
                setSelectedFile(file);
            } else {
                showNotification(`Only ${getFileTypeMessage()} files are allowed!`, 'error');
                event.target.value = null;
            }
        }
    };

    const handleSheetNameChange = (event) => {
        const value = event.target.value;
        setSheetName(value);
        if (value.trim()) {
            setSheetNameError("");
        } else {
            setSheetNameError("Sheet name is required");
        }
    };

    const validateSheetName = () => {
        if (selectedBox?.requiresSheetName && !sheetName.trim()) {
            setSheetNameError("Sheet name is required");
            return false;
        }
        return true;
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            showNotification('Please select a file to upload!', 'error');
            return;
        }

        if (!validateSheetName()) return;

        setUploading(true);

        try {
            const formData = new FormData();
            formData.append('file', selectedFile);

            const apiCall = apiCalls[selectedBox.apiMethod];
            if (!apiCall) throw new Error('API method not found');

            const sheetNameValue = selectedBox.requiresSheetName ? sheetName.trim() : "";
            const response = await apiCall(selectedFile, formData, sheetNameValue);
            console.log(response);

            if (response.data.success || response.status === 200) {
                showNotification(`${selectedBox.title} uploaded successfully!`, 'success');
                handleCloseModal();
            } else {
                throw new Error(response.data.message || 'Upload failed');
            }
        } catch (error) {
            console.error('Upload error:', error);
            showNotification(error?.response?.data?.error || error.message || 'Failed to upload file', 'error');
        } finally {
            setUploading(false);
        }
    };

    const getFileIcon = () => {
        if (!selectedFile) return null;
        const extension = selectedFile.name.split('.').pop().toLowerCase();
        switch (extension) {
            case 'csv':  return <DescriptionIcon sx={{ color: '#2e7d32' }} />;
            case 'xls':  return <DescriptionIcon sx={{ color: '#1976d2' }} />;
            case 'xlsx': return <DescriptionIcon sx={{ color: '#ed6c02' }} />;
            default:     return <DescriptionIcon color='primary' />;
        }
    };

    return (
        <Fragment>
            <Box mt={1} mb={3}>
                <Typography variant='h4' style={{ fontWeight: 'bold' }}>Import Data</Typography>
                <Typography variant='body2' color='textSecondary' mt={1}>
                    Upload Excel files (CSV, XLS, XLSX) to import data into the system
                </Typography>
            </Box>

            {/* Box Grid */}
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' },
                gap: 3,
                mt: 2
            }}>
                {boxes.map((box) => (
                    <Card
                        key={box.id}
                        sx={{
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: 6
                            },
                            borderTop: `4px solid ${box.color}`
                        }}
                        onClick={() => handleBoxClick(box)}
                    >
                        <CardContent sx={{ textAlign: 'center', py: 3 }}>
                            <Box sx={{ color: box.color, mb: 2 }}>
                                {box.icon}
                            </Box>
                            <Typography variant='h6' component='div' sx={{ fontWeight: 'medium' }}>
                                {box.title}
                            </Typography>
                            <Typography variant='caption' color='textSecondary' sx={{ mt: 1, display: 'block' }}>
                                Click to import
                            </Typography>

                            {/* ── Sample download button ── */}
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
                                onClick={(e) => handleSampleDownload(e, box)}
                                sx={{
                                    mt: 1.5,
                                    fontSize: 11,
                                    textTransform: 'none',
                                    borderColor: box.color,
                                    color: box.color,
                                    '&:hover': {
                                        borderColor: box.color,
                                        bgcolor: `${box.color}12`,
                                    }
                                }}
                            >
                                Sample File
                            </Button>
                        </CardContent>
                    </Card>
                ))}
            </Box>

            {/* Upload Modal */}
            <Modal
                open={openModal}
                onClose={handleCloseModal}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{ timeout: 500 }}
            >
                <Fade in={openModal}>
                    <Paper sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: { xs: '90%', sm: 500 },
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 4,
                        borderRadius: 2
                    }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant='h5' component='h2' sx={{ fontWeight: 'bold' }}>
                                {selectedBox?.title}
                            </Typography>
                            <IconButton onClick={handleCloseModal} size='small'>
                                <CloseIcon />
                            </IconButton>
                        </Box>

                        <Typography variant='body2' color='textSecondary' sx={{ mb: 3 }}>
                            Please select a file (CSV, XLS, or XLSX) to upload
                        </Typography>

                        <Box sx={{ mb: 3 }}>
                            <input
                                accept=".csv,.xls,.xlsx"
                                style={{ display: 'none' }}
                                id="file-upload"
                                type="file"
                                onChange={handleFileSelect}
                            />
                            <label htmlFor="file-upload">
                                <Button
                                    variant="outlined"
                                    component="span"
                                    startIcon={<CloudUploadIcon />}
                                    fullWidth
                                    sx={{ py: 2 }}
                                >
                                    {selectedFile ? selectedFile.name : 'Choose File'}
                                </Button>
                            </label>
                        </Box>

                        {selectedFile && (
                            <Box sx={{
                                mb: 3,
                                p: 2,
                                bgcolor: '#f5f5f5',
                                borderRadius: 1,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}>
                                {getFileIcon()}
                                <Typography variant='body2'>
                                    {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                                </Typography>
                            </Box>
                        )}

                        {/* Sheet Name Input */}
                        {selectedBox?.requiresSheetName && (
                            <Box sx={{ mb: 3 }}>
                                <TextField
                                    fullWidth
                                    label="Sheet Name"
                                    variant="outlined"
                                    value={sheetName}
                                    onChange={handleSheetNameChange}
                                    error={!!sheetNameError}
                                    helperText={sheetNameError || "Enter the sheet name for this import"}
                                    required
                                    disabled={uploading}
                                    placeholder="e.g., Sheet1"
                                />
                            </Box>
                        )}

                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                            <Button variant='outlined' onClick={handleCloseModal} disabled={uploading}>
                                Cancel
                            </Button>
                            <Button
                                variant='contained'
                                onClick={handleUpload}
                                disabled={!selectedFile || uploading || (selectedBox?.requiresSheetName && !sheetName.trim())}
                                sx={{
                                    bgcolor: selectedBox?.color,
                                    '&:hover': { bgcolor: selectedBox?.color, opacity: 0.9 }
                                }}
                            >
                                {uploading ? 'Uploading...' : 'Upload'}
                            </Button>
                        </Box>
                    </Paper>
                </Fade>
            </Modal>
        </Fragment>
    );
};

export default ImportData;